import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportAutoError } from '../services/autoErrorReporter';

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    void reportAutoError({
      message: error.message || 'Application crashed',
      details: [error.stack, info.componentStack].filter(Boolean).join('\n'),
      action: 'Screen crashed (React error boundary)',
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 p-8 text-center text-slate-800">
        <p className="max-w-md text-base font-medium">Something went wrong while showing this page.</p>
        <p className="max-w-md text-sm text-slate-600">Our team has been notified. Reload the page to continue.</p>
        <button
          type="button"
          className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800"
          onClick={() => window.location.reload()}
        >
          Reload page
        </button>
      </div>
    );
  }
}
